import _ from "lodash";
import {
  FIRST_NAME,
  LAST_NAME,
  NAME_MAPPING,
  DEFAULT_NAME_ORDER_PREFERENCE,
  FULL_NAME,
} from "Constants/stringConstants";
import {
  ENABLE_USER_MIDDLE_NAME,
  STAFF_NAME_ORDER,
  STAFF_PRINT_NAME_ORDER,
} from "Constants/permissionConstants";
import { getCleanNameOption } from "./GetCleanNameOption";
import { parseConfigurationValue } from "./Common";
import ACLStore from "lib/aclStore";
import { getSettingValue } from "modules/PermissionModule";

const _getDefaultOrderBy = () => {
  const orderByConfig = ACLStore.getConfigObj("ShowNameOrderedBy");
  const orderBy = parseConfigurationValue(orderByConfig) || FIRST_NAME;

  return orderBy;
};

const _getDefaultStaffNameOrderPreference = () => {
  const defaultStaffNameOrderPreference =
    getSettingValue({
      name: STAFF_NAME_ORDER,
      readFromUserAndOrgLevel: true,
    }) || DEFAULT_NAME_ORDER_PREFERENCE;

  return defaultStaffNameOrderPreference;
};

export const getDefaultStaffNameOrderPreference = _.memoize(
  _getDefaultStaffNameOrderPreference,
  params => JSON.stringify(params)
);

const _getDefaultStaffPrintNameOrderPreference = () => {
  const defaultStaffPrintNameOrderPreference =
    getSettingValue({
      name: STAFF_PRINT_NAME_ORDER,
      readFromUserAndOrgLevel: true,
    }) || DEFAULT_NAME_ORDER_PREFERENCE;

  return defaultStaffPrintNameOrderPreference;
};

const getDefaultStaffPrintNameOrderPreference = _.memoize(
  _getDefaultStaffPrintNameOrderPreference,
  params => JSON.stringify(params)
);

export const getDefaultOrderBy = _.memoize(_getDefaultOrderBy, params =>
  JSON.stringify(params)
);

export const getFormattedStaffName = ({ staff, orderBy = FIRST_NAME }) => {
  const { firstName, lastName } = _.get(staff, "node", {});

  const staffName = _.isEqual(orderBy, LAST_NAME)
    ? `${lastName}, ${firstName}`
    : `${firstName} ${lastName}`;

  return staffName;
};

const getDefaultNameOrderPreferenceForStaff = shouldGeneratePrintOrder => {
  let defaultStudentNameOrderPreference = [];
  if (shouldGeneratePrintOrder) {
    defaultStudentNameOrderPreference = getDefaultStaffPrintNameOrderPreference();
  } else {
    defaultStudentNameOrderPreference = getDefaultStaffNameOrderPreference();
  }
  return defaultStudentNameOrderPreference;
};

export const getFormattedStaffNameV2 = ({
  staff,
  shouldGeneratePrintOrder = false,
  isVisitor = false,
}) => {
  const { firstName = "", lastName = "", role = "" } = staff || {};
  let staffName = "";

  const isUserVisitor = isVisitor || role === "visitor";

  if (ACLStore.can(ENABLE_USER_MIDDLE_NAME) && !isUserVisitor) {
    const defaultStaffNameOrderPreference = getDefaultNameOrderPreferenceForStaff(
      shouldGeneratePrintOrder
    );

    defaultStaffNameOrderPreference.forEach(order => {
      const isInitial = _.includes(order, "_INITIAL");
      const cleanOrder = getCleanNameOption(order, true);
      const propertyName = NAME_MAPPING[cleanOrder];
      const propertyValue = _.get(staff, propertyName, "");

      if (propertyValue) {
        if (staffName) {
          staffName += " ";
        }
        if (isInitial) {
          const cleanOrderWithInitial = getCleanNameOption(order);
          staffName += _.replace(
            order,
            cleanOrderWithInitial,
            propertyValue.charAt(0)
          );
        } else {
          staffName += _.replace(order, cleanOrder, propertyValue);
        }
      }
    });
  } else {
    const orderBy = getDefaultOrderBy();
    staffName = _.isEqual(orderBy, LAST_NAME)
      ? `${lastName}, ${firstName}`
      : `${firstName} ${lastName}`;
  }

  return staffName;
};

export const getStaffIdentifierName = ({ staff, isVisitor }) => {
  if (ACLStore.can(ENABLE_USER_MIDDLE_NAME)) {
    return getFormattedStaffNameV2({ staff, isVisitor });
  }

  const defaultPropertyName = NAME_MAPPING[FIRST_NAME];
  const defaultValue = _.get(staff, defaultPropertyName, "");

  return defaultValue;
};

export const getStaffOrderBy = props => {
  const enableUserMiddleName = ACLStore.can(ENABLE_USER_MIDDLE_NAME);
  const { isVisitor = false } = props || {};

  if (!enableUserMiddleName || isVisitor) {
    return getDefaultOrderBy();
  }

  return FULL_NAME;
};

export const getStaffSortByList = ({
  sortOptions = [],
  keyToMap = "",
  isVisitor,
}) => {
  const isUserMiddleNameEnabled = ACLStore.can(ENABLE_USER_MIDDLE_NAME);
  const defaultNameOrder = getDefaultStaffNameOrderPreference();
  const cleanNameOrder = _.map(defaultNameOrder, order =>
    getCleanNameOption(order, true)
  );
  const NAME_ORDER_PREFERENCE = [FULL_NAME];
  if (_.includes(cleanNameOrder, FIRST_NAME)) {
    NAME_ORDER_PREFERENCE.push(FIRST_NAME);
  }
  if (_.includes(cleanNameOrder, LAST_NAME)) {
    NAME_ORDER_PREFERENCE.push(LAST_NAME);
  }
  const staffNameOrderPreference =
    isUserMiddleNameEnabled && !isVisitor
      ? NAME_ORDER_PREFERENCE
      : DEFAULT_NAME_ORDER_PREFERENCE;

  let updatedSortList = sortOptions;
  updatedSortList = _.filter(sortOptions, sortOption =>
    _.includes(staffNameOrderPreference, _.get(sortOption, keyToMap))
  );
  updatedSortList = _.sortBy(updatedSortList, sortItem =>
    _.findIndex(
      staffNameOrderPreference,
      key => key === _.get(sortItem, keyToMap)
    )
  );
  return updatedSortList;
};
